import { NgModule } from '@angular/core';
import { RouterModule } from '@angular/router';

import { bulkTcsDeactivateGuard } from './guards/BulkSubmissionsDeactivate/bulk-tcs-deactivate.guard';
import { canActivateRouteGuard } from './guards/can-activate/can-activate-route.guard';
import { editDocDeactivateGuard } from './guards/EditDocDeactivate/edit-doc-deactivate.guard';
import { editTemplateDeactivateGuard } from './guards/EditTemplateDeactivate/edit-template-deactivate.guard';
import { EditDocumentComponent } from './pages/edit-document/edit-document.component';
import { EditTemplateComponent } from './pages/edit-template/edit-template.component';
import { Acp4Routes } from './types/app-routes.type';

const routes: Acp4Routes = [
  {
    path: 'login',
    loadComponent: () => import('./pages/login/login.component')
    .then(m => m.LoginComponent),
    title: 'Login',
  },
  /**'invite' it used ONLY for System registration
   * 'registraion' is ONLY for new User registration
   * GUID will fail if used with the wrong path */
  {
    path: 'invite/:GUID',
    loadComponent: () => import('./components/invite/invite.component')
      .then(m => m.InviteComponent),
    title: 'Invite',
  },
  {
    path: 'registration/:GUID',
    loadComponent: () => import('./components/invite/invite.component')
      .then(m => m.InviteComponent),
    title: 'Registration',
  },
  {
    path: 'forgot-password',
    loadComponent: () => import('./pages/forgot-password/forgot-password.component')
      .then(m => m.ForgotPasswordComponent),
    title: 'Forgot Password',
  },
  {
    path: 'reset-password/:GUID',
    loadComponent: () => import('./pages/reset-password/reset-password.component')
      .then(m => m.ResetPasswordComponent),
    title: 'Reset Password',
  },
  {
    path: 'dashboard',
    loadComponent: () => import('./components/dashboard/dashboard.component')
      .then(m => m.DashboardComponent),
    canActivate: [canActivateRouteGuard],
    title: 'Dashboard',
    data: {
      navBreadcrumbsTitle: 'Home',
    },
  },
  {
    path: 'bo-profiles',
    canActivate: [canActivateRouteGuard],
    title: 'BO Profiles',
    data: {
      navBreadcrumbsTitle: 'BO Profiles',
      requiredPermission: 'bo-profiles:view',
    },
    children: [
      {
        path: '',
        loadComponent: () => import('./pages/bo-profiles/bo-profiles.component')
          .then(m => m.BOProfilesComponent),
        canActivate: [canActivateRouteGuard],
      },
      {
        path: 'bulk-upload',
        loadComponent: () => import('./pages/bo-profiles/bulk-upload-bo-profiles/bulk-upload-bo-profiles-manager.component')
          .then(m => m.BulkUploadBoProfilesComponent),
        canActivate: [canActivateRouteGuard],
        title: 'Bulk upload BO Profiles',
        data: {
          navBreadcrumbsTitle: 'Bulk upload',
          requiredPermission: 'bo-profiles:bulk-upload',
        },
      },
    ],
  },
  {
    path: 'documents',
    loadComponent: () => import('./pages/documents/documents.component')
      .then(m => m.DocumentsComponent),
    canActivate: [canActivateRouteGuard],
    title: 'Documents',
    data: {
      navBreadcrumbsTitle: 'Documents',
      requiredPermission: 'documents:view',
    },
  },
  {
    path: 'document-processing-digital',
    loadComponent: () => import('./pages/documents/doc-processing-page/doc-processing.component')
      .then(m => m.DocProcessingComponent),
    canActivate: [canActivateRouteGuard],
    title: 'Documents',
    data: {
      navBreadcrumbsTitle: 'Digital Document Processing',
      requiredPermission: 'documents:processdigital',
    },
  },
  {
    path: 'document-processing-paper',
    loadComponent: () => import('./pages/documents/doc-processing-page/doc-processing.component')
      .then(m => m.DocProcessingComponent),
    canActivate: [canActivateRouteGuard],
    title: 'Documents',
    data: {
      navBreadcrumbsTitle: 'Paper Document Processing',
      requiredPermission: 'documents:processpaper',
    },
  },
  {
    path: 'security-position-reports',
    loadComponent: () => import('./pages/reporting/security-position-reports/security-position-reports.component')
      .then(m => m.SecurityPositionReportsComponent),
    canActivate: [canActivateRouteGuard],
    title: 'Security Position Reports',
    data: {
      navBreadcrumbsTitle: 'Security Position Reports',
      requiredPermission: 'spr:view',
    },
  },
  {
    path: 'payment-reports',
    loadComponent: () => import('./pages/reporting/payment-maturity/payment-maturity-reports.component')
      .then(m => m.PaymentMaturityReportsComponent),
    canActivate: [canActivateRouteGuard],
    title: 'Payment & Maturity Reporting',
    data: {
      navBreadcrumbsTitle: 'Payment & Maturity Reporting',
      requiredPermission: 'paymentmaturity:view',
    },
  },

  /*===== TAX CERTIFICATION TAB =====*/
  {
    path: 'tax-certification-submissions',
    canActivate: [canActivateRouteGuard],
    title: 'Tax Certification Submissions',
    data: {
      navBreadcrumbsTitle: 'Tax Certification Submissions',
      requiredPermission: 'taxcert:view',
    },
    children: [
      {
        path: '',
        loadComponent: () => import('./pages/tax-certification/tax-certification-submissions/tax-certification-submissions.component')
          .then(m => m.TaxCertificationSubmissionsComponent),
        canActivate: [canActivateRouteGuard],
      },
      {
        path: 'bulk-upload',
        loadComponent: () => import('./pages/tax-certification/tax-certification-submissions/bulk-upload-tcs/bulk-upload-tcs.component')
          .then(m => m.BulkUploadTcsComponent),
        canActivate: [canActivateRouteGuard],
        canDeactivate: [bulkTcsDeactivateGuard],
        title: 'Bulk upload TCS',
        data: {
          navBreadcrumbsTitle: 'Bulk upload',
          requiredPermission: 'taxcert:bulk-upload',
        },
      },
      {
        path: 'document-status',
        loadComponent: () => import('./pages/tax-certification/tcs-doc-status/tcs-doc-status.component')
          .then(m => m.TcsDocStatusComponent),
        canActivate: [canActivateRouteGuard],
        title: 'Tax Certification Submission Document Status',
        data: {
          navBreadcrumbsTitle: 'Tax Certification Submission Document Status',
          requiredPermission: 'taxcert:view',
        },
      },
    ],
  },
  {
    path: 'add-tcs',
    canActivate: [canActivateRouteGuard],
    title: 'New Tax Certification Submission',
    data: {
      navBreadcrumbsTitle: 'New Tax Certification Submission',
      requiredPermission: 'taxcert:add',
    },
    children: [
      {
        path: '',
        loadComponent: () => import('./pages/tax-certification/tax-certification-submissions/new-single-tcs/new-single-tcs.component')
          .then(m => m.NewSingleTcsComponent),
        canActivate: [canActivateRouteGuard],
      },
      {
        path: 'success',
        loadComponent: () => import('./pages/tax-certification/tax-certification-submissions/new-single-tcs/add-tcs-complete/add-tcs-complete.component')
          .then(m => m.AddTcsCompleteComponent),
        canActivate: [canActivateRouteGuard],
        title: 'Tax Certification Submission Complete',
        data: {
          navBreadcrumbsTitle: 'Tax Certification Submission Complete',
          requiredPermission: 'taxcert:view',
        },
      },
    ],
  },
  {
    path: 'tax-certification-submissions-data',
    loadComponent: () => import('./pages/tax-certification/tax-certification-submissions-data/tax-certification-submissions-data.component')
      .then(m => m.TaxCertificationSubmissionsDataComponent),
    canActivate: [canActivateRouteGuard],
    title: 'Tax Certification Submissions Data',
    data: {
      navBreadcrumbsTitle: 'Tax Certification Submissions Data',
      requiredPermission: 'taxcert:view',
    },
  },
  {
    path: 'downstream-positions',
    loadComponent: () => import('./pages/tax-certification/downstream-positions/downstream-positions.component')
      .then(m => m.DownstreamPositionsComponent),
    canActivate: [canActivateRouteGuard],
    title: 'Downstream Position Confirmations',
    data: {
      navBreadcrumbsTitle: 'Downstream Position Confirmations',
      requiredPermission: 'downstreampositions:view',
    },
  },

  /*===== REPORTING TAB =====*/
  {
    path: 'participant-positions-overview',
    loadComponent: () => import('./pages/reporting/participant-positions-overview/participant-positions-overview.component')
      .then(m => m.ParticipantPositionsOverviewComponent),
    canActivate: [canActivateRouteGuard],
    title: 'Participant Positions Overview',
    data: {
      navBreadcrumbsTitle: 'Participant Positions Overview',
      requiredPermission: 'pcp_act_ovw:view',
    },
  },
  {
    path: 'participant-positions-history',
    loadComponent: () => import('./pages/reporting/participant-positions-history/participant-positions-history/participant-positions-history.component')
      .then(m => m.ParticipantPositionsHistoryComponent),
    canActivate: [canActivateRouteGuard],
    title: 'Participant Positions History',
    data: {
      navBreadcrumbsTitle: 'Participant Positions History',
      requiredPermission: 'pcp_act_hist:view',
    },
  },
  {
    path: 'bo-positions-overview',
    loadComponent: () => import('./pages/reporting/bo-positions-overview/bo-positions-overview.component')
      .then(m => m.BoPositionsOverviewComponent),
    canActivate: [canActivateRouteGuard],
    title: 'Beneficial Owner Positions Overview / Quick Updates',
    data: {
      navBreadcrumbsTitle: 'Beneficial Owner Positions Overview / Quick Updates',
      requiredPermission: 'bo-positions:view',
    },
  },
  {
    path: 'bo-positions-history',
    loadComponent: () => import('./pages/reporting/beneficial-owner/beneficial-owner-history/bo-positions-history.component')
      .then(m => m.BOPositionsHistoryComponent),
    canActivate: [canActivateRouteGuard],
    title: 'Beneficial Owner Positions History',
    data: {
      navBreadcrumbsTitle: 'Beneficial Owner Positions History',
      requiredPermission: 'bo-history:view',
    },
  },
  {
    path: 'tax-liabilities',
    loadComponent: () => import('./pages/reporting/tax-liabilities-debits-credits/tax-liabilities-debits-credits.component')
      .then(m => m.TaxLiabilitiesDebitsCreditsComponent),
    canActivate: [canActivateRouteGuard],
    title: 'Tax Liabilities, Debits & Credits',
    data: {
      navBreadcrumbsTitle: 'Tax Liabilities, Debits & Credits',
      requiredPermission: 'taxliability:view',
    },
  },
  {
    path: 'sanctions-files',
    loadComponent: () => import('./pages/reporting/sanctions-raw-files/sanctions-raw-files.component')
      .then(m => m.SanctionsRawFilesComponent),
    canActivate: [canActivateRouteGuard],
    title: 'Sanctions Raw Files',
    data: {
      navBreadcrumbsTitle: 'Sanctions Raw Files',
      requiredPermission: 'sanction_screening_results:view',
    },
  },
  {
    path: 'sanctions-results',
    loadComponent: () => import('./pages/reporting/sanction-screening-results/sanction-screening-results.component')
      .then(m => m.SanctionScreeningResultsComponent),
    canActivate: [canActivateRouteGuard],
    title: 'Sanction Screening Results',
    data: {
      navBreadcrumbsTitle: 'Sanction Screening Results',
      requiredPermission: 'sanction_screening_results:view',
    },
  },
  {
    path: 'compliance-reports-sogei',
    loadComponent: () => import('./pages/reporting/compliance-reporting-sogei/compliance-reporting-sogei.component')
      .then(m => m.ComplianceReportingSogeiComponent),
    canActivate: [canActivateRouteGuard],
    title: 'Compliance Reporting - SOGEI',
    data: {
      navBreadcrumbsTitle: 'Compliance Reporting - SOGEI',
      requiredPermission: 'sogei_compliance:view',
    },
  },
  /*==========*/

  {
    path: 'audits',
    loadComponent: () => import('./pages/audits/audits.component')
      .then(m => m.AuditsComponent),
    canActivate: [canActivateRouteGuard],
    title: 'Audits',
    data: {
      navBreadcrumbsTitle: 'Audits',
      requiredPermission: 'audits:view',
    },
  },
  {
    path: 'edit-document',
    component: EditDocumentComponent,
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    canActivate: [canActivateRouteGuard],
    canDeactivate: [editDocDeactivateGuard],
    title: 'Edit document',
  },
  {
    path: 'statutory-rates',
    canActivate: [canActivateRouteGuard],
    title: 'Statutory Rates',
    data: {
      navBreadcrumbsTitle: 'Statutory Rates',
      requiredPermission: 'mdt:view-statutory-rates',
    },
    children: [
      {
        path: '',
        loadComponent: () => import('./pages/manage/statutory-rates/statutory-rates.component')
          .then(m => m.StatutoryRatesComponent),
        canActivate: [canActivateRouteGuard],
      },
      {
        path: 'bulk-upload',
        loadComponent: () => import('./pages/manage/statutory-rates/statutory-rates-bulk-upload/statutory-rates-bulk-upload.component')
          .then(m => m.StatutoryRatesBulkUploadComponent),
        canActivate: [canActivateRouteGuard],
        title: 'Statutory Rates bulk upload',
        data: {
          navBreadcrumbsTitle: 'Statutory Rates Bulk upload',
          requiredPermission: 'mdt:update-statutory-rates',
        },
      },
    ],
  },
  {
    path: 'internal-bo-types',
    loadComponent: () => import('./pages/manage/internal-bo-types/internal-bo-types/internal-bo-types.component')
      .then(m => m.InternalBoTypesComponent),
    canActivate: [canActivateRouteGuard],
    title: 'Internal BO Types',
    data: {
      navBreadcrumbsTitle: 'BO Types',
      requiredPermission: 'market-info:view',
    },
  },
  {
    path: 'internal-config',
    canActivate: [canActivateRouteGuard],
    title: 'Internal Config',
    data: {
      navBreadcrumbsTitle: 'Treaty Rates',
      requiredPermission: 'mdt:view-internal-config',
    },
    children: [
      {
        path: '',
        loadComponent: () => import('./pages/manage/internal-config/treaty-rates.component')
          .then(m => m.TreatyRatesComponent),
        canActivate: [canActivateRouteGuard],
      },
      {
        path: 'bulk-upload',
        loadComponent: () => import('./pages/manage/internal-config/internal-config-upload/internal-config-upload.component')
          .then(m => m.InternalConfigUploadComponent),
        canActivate: [canActivateRouteGuard],
        title: 'Treaty Rates bulk upload',
        data: {
          navBreadcrumbsTitle: 'Bulk upload',
          requiredPermission: 'mdt:update-internal-config',
        },
      },
    ],
  },
  {
    path: 'external-config',
    canActivate: [canActivateRouteGuard],
    title: 'External BO Types',
    data: {
      navBreadcrumbsTitle: 'External BO Types',
      requiredPermission: 'mdt:view-external-config',
    },
    children: [
      {
        path: '',
        loadComponent: () => import('./pages/manage/external-config/external-bo-types.component')
          .then(m => m.ExternalBOTypesComponent),
        canActivate: [canActivateRouteGuard],
      },
      {
        path: 'bulk-upload',
        loadComponent: () => import('./pages/manage/external-config/external-config-upload/external-config-upload.component')
          .then(m => m.ExternalConfigUploadComponent),
        canActivate: [canActivateRouteGuard],
        title: 'External BO Types bulk upload',
        data: {
          navBreadcrumbsTitle: 'External Config Bulk upload',
          requiredPermission: 'mdt:update-external-config',
        },
      },
    ],
  },
  {
    path: 'document-validity',
    canActivate: [canActivateRouteGuard],
    title: 'Document Validity',
    data: {
      navBreadcrumbsTitle: 'Document Validity',
      requiredPermission: 'mdt:view-doc-validity',
    },
    children: [
      {
        path: '',
        loadComponent: () => import('./pages/manage/doc-validity/doc-validity.component')
          .then(m => m.DocValidityComponent),
        canActivate: [canActivateRouteGuard],
      },
      {
        path: 'bulk-upload',
        loadComponent: () => import('./pages/manage/doc-validity/doc-validity-upload/doc-validity-upload.component')
          .then(m => m.DocValidityUploadComponent),
        canActivate: [canActivateRouteGuard],
        title: 'Document validity bulk upload',
        data: {
          navBreadcrumbsTitle: 'Document validity Bulk upload',
          requiredPermission: 'mdt:update-doc-validity',
        },
      },
    ],
  },
  {
    path: 'statute-of-limitations',
    canActivate: [canActivateRouteGuard],
    title: 'Statute Of Limitations',
    data: {
      navBreadcrumbsTitle: 'Statute Of Limitations',
      requiredPermission: 'mdt:view-statute-limit',
    },
    children: [
      {
        path: '',
        loadComponent: () => import('./pages/manage/statute-of-limitations/statute-of-limitations.component')
          .then(m => m.StatuteOfLimitationComponent),
        canActivate: [canActivateRouteGuard],
      },
      {
        path: 'bulk-upload',
        loadComponent: () => import('./pages/manage/statute-of-limitations/statute-of-limitations-upload/statute-of-limitations-upload.component')
          .then(m => m.StatuteOfLimitationsUploadComponent),
        canActivate: [canActivateRouteGuard],
        title: 'Statute Of Limitations bulk upload',
        data: {
          navBreadcrumbsTitle: 'Bulk upload',
          requiredPermission: 'mdt:update-statute-limit',
        },
      },
    ],
  },
  {
    path: 'reclaim-start-dates',
    canActivate: [canActivateRouteGuard],
    title: 'Reclaim Start Dates',
    data: {
      navBreadcrumbsTitle: 'Reclaim Start Dates',
      requiredPermission: 'mdt:view-reclaim-start-dates',
    },
    children: [
      {
        path: '',
        loadComponent: () => import('./pages/manage/reclaim-start-dates/reclaim-start-dates.component')
          .then(m => m.ReclaimStartDatesComponent),
        canActivate: [canActivateRouteGuard],
      },
      {
        path: 'bulk-upload',
        loadComponent: () => import('./pages/manage/reclaim-start-dates/reclaim-start-dates-upload/reclaim-start-dates-upload.component')
          .then(m => m.ReclaimStartDatesUploadComponent),
        canActivate: [canActivateRouteGuard],
        title: 'Reclaim Start Dates bulk upload',
        data: {
          navBreadcrumbsTitle: 'Bulk upload',
          requiredPermission: 'mdt:update-reclaim-start-dates',
        },
      },
    ],
  },
  {
    path: 'document-types',
    canActivate: [canActivateRouteGuard],
    title: 'Document Types',
    data: {
      navBreadcrumbsTitle: 'Document Types',
      requiredPermission: 'mdt:view-document-types',
    },
    children: [
      {
        path: '',
        loadComponent: () => import('./pages/manage/document-types/document-types.component')
          .then(m => m.DocumentTypesComponent),
        canActivate: [canActivateRouteGuard],
      },
      {
        path: 'bulk-upload',
        loadComponent: () => import('./pages/manage/document-types/document-types-upload/document-types-upload.component')
          .then(m => m.DocumentTypesUploadComponent),
        canActivate: [canActivateRouteGuard],
        title: 'Document Types bulk upload',
        data: {
          navBreadcrumbsTitle: 'Bulk upload',
          requiredPermission: 'mdt:update-document-types',
        },
      },
    ],
  },
  {
    path: 'document-retrospectives',
    canActivate: [canActivateRouteGuard],
    title: 'Document Retrospectives',
    data: {
      navBreadcrumbsTitle: 'Document Retrospectives',
      requiredPermission: 'mdt:view-retroactive-types',
    },
    children: [
      {
        path: '',
        loadComponent: () => import('./pages/manage/document-retrospectives/document-retrospectives.component')
          .then(m => m.DocumentRetrospectivesComponent),
        canActivate: [canActivateRouteGuard],
      },
      {
        path: 'bulk-upload',
        loadComponent: () => import('./pages/manage/document-retrospectives/document-retrospectives-upload/document-retrospectives-upload.component')
          .then(m => m.DocumentRetrospectivesUploadComponent),
        canActivate: [canActivateRouteGuard],
        title: 'Document Retrospectives bulk upload',
        data: {
          navBreadcrumbsTitle: 'Bulk upload',
          requiredPermission: 'mdt:update-retroactive-types',
        },
      },
    ],
  },
  {
    path: 'required-documents',
    canActivate: [canActivateRouteGuard],
    title: 'Required Documents',
    data: {
      navBreadcrumbsTitle: 'Required Documents',
      requiredPermission: 'mdt:view-required-documents',
    },
    children: [
      {
        path: '',
        loadComponent: () => import('./pages/manage/required-documents/required-documents.component')
          .then(m => m.RequiredDocumentsComponent),
        canActivate: [canActivateRouteGuard],
      },
      {
        path: 'bulk-upload',
        loadComponent: () => import('./pages/manage/required-documents/required-documents-upload/required-documents-upload.component')
          .then(m => m.RequiredDocumentsUploadComponent),
        canActivate: [canActivateRouteGuard],
        title: 'Required Documents bulk upload',
        data: {
          navBreadcrumbsTitle: 'Bulk upload',
          requiredPermission: 'mdt:update-required-documents',
        },
      },
    ],
  },
  {
    path: 'companies',
    canActivate: [canActivateRouteGuard],
    title: 'Companies',
    data: {
      navBreadcrumbsTitle: 'Companies',
      requiredPermission: 'company:view',
    },
    children: [
      {
        path: '',
        loadComponent: () => import('./pages/manage/companies/companies.component')
          .then(m => m.CompaniesComponent),
        canActivate: [canActivateRouteGuard],
      },
    ],
  },
  {
    path: 'users',
    loadComponent: () => import('./pages/manage/users/users.component')
      .then(m => m.UsersComponent),
    canActivate: [canActivateRouteGuard],
    title: 'Users',
    data: {
      navBreadcrumbsTitle: 'Users',
      requiredPermission: 'users:view',
    },
  },
  {
    path: 'roles',
    loadComponent: () => import('./pages/manage/roles/roles.component')
      .then(m => m.RolesComponent),
    canActivate: [canActivateRouteGuard],
    title: 'Roles',
    data: {
      navBreadcrumbsTitle: 'Roles',
      requiredPermission: 'roles:view',
    },
  },
  {
    path: 'automated-emails',
    canActivate: [canActivateRouteGuard],
    title: 'Automated Emails',
    data: {
      navBreadcrumbsTitle: 'Automated Emails',
      requiredPermission: 'email-types:view',
    },
    children: [
      {
        path: '',
        loadComponent: () => import('./pages/manage/automated-emails/automated-emails.component')
          .then(m => m.AutomatedEmailsComponent),
        canActivate: [canActivateRouteGuard],
      },
      {
        path: 'view',
        loadComponent: () => import('./pages/manage/automated-emails/create/create-automated-email.component')
          .then(m => m.CreateAutomatedEmailComponent),
        canActivate: [canActivateRouteGuard],
        data: {
          navBreadcrumbsTitle: 'View / Edit',
          requiredPermission: 'email-types:update',
        },
      },
      {
        path: 'create',
        loadComponent: () => import('./pages/manage/automated-emails/create/create-automated-email.component')
          .then(m => m.CreateAutomatedEmailComponent),
        canActivate: [canActivateRouteGuard],
        data: {
          navBreadcrumbsTitle: 'Create',
          requiredPermission: 'email-types:add',
        },
      },
    ],
  },
  {
    path: 'projects',
    canActivate: [canActivateRouteGuard],
    title: 'Projects',
    data: {
      navBreadcrumbsTitle: 'Projects',
      requiredPermission: 'projects:view',
    },
    children: [
      {
        path: '',
        loadComponent: () => import('./pages/manage/projects/projects.component')
          .then(m => m.ProjectsComponent),
        canActivate: [canActivateRouteGuard],
      },
    ],
  },
  {
    path: 'securities-data',
    canActivate: [canActivateRouteGuard],
    title: 'Securities Data',
    data: {
      navBreadcrumbsTitle: 'Securities Data',
      requiredPermission: 'secdata:view',
    },
    children: [
      {
        path: '',
        loadComponent: () => import('./pages/manage/securities-data/securities-data.component')
          .then(m => m.SecuritiesDataComponent),
        canActivate: [canActivateRouteGuard],
      },
      {
        path: 'bulk-upload',
        loadComponent: () => import('./pages/manage/securities-data/bulk-upload-securities/bulk-upload-securities-manager.component')
          .then(m => m.BulkUploadSecuritiesManagerComponent),
        canActivate: [canActivateRouteGuard],
        title: 'Bulk upload Securities Data',
        data: {
          navBreadcrumbsTitle: 'Bulk upload',
          requiredPermission: 'secdata:add',
        },
      },
    ],
  },
  {
    path: 'corporate-actions',
    canActivate: [canActivateRouteGuard],
    title: 'Corporate Actions',
    data: {
      navBreadcrumbsTitle: 'Corporate Actions',
      requiredPermission: 'caevent:view',
    },
    children: [
      {
        path: '',
        loadComponent: () => import('./pages/manage/corporate-actions/corporate-actions.component')
          .then(m => m.CorporateActionsComponent),
        canActivate: [canActivateRouteGuard],
      },
      {
        path: 'bulk-upload',
        loadComponent: () => import('./pages/manage/corporate-actions/bulk-upload-corporate-actions/bulk-upload-corporate-actions.component')
          .then(m => m.BulkUploadCorporateActionsComponent),
        canActivate: [canActivateRouteGuard],
        title: 'Bulk upload Corporate Actions',
        data: {
          navBreadcrumbsTitle: 'Bulk upload',
          requiredPermission: 'caevent:add',
        },
      },
    ],
  },
  {
    path: 'accounts',
    canActivate: [canActivateRouteGuard],
    title: 'Accounts',
    data: {
      navBreadcrumbsTitle: 'Accounts',
      requiredPermission: 'accounts:view',
    },
    children: [
      {
        path: '',
        loadComponent: () => import('./pages/manage/accounts/accounts.component')
          .then(m => m.AccountsComponent),
        canActivate: [canActivateRouteGuard],
      },
    ],
  },
  {
    path: 'authorized-signatories',
    canActivate: [canActivateRouteGuard],
    title: 'Authorized Signatories',
    data: {
      navBreadcrumbsTitle: 'Authorized Signatories',
      requiredPermission: 'signatory:view',
    },
    children: [
      {
        path: '',
        loadComponent: () => import('./pages/manage/authorized-signatories/authorized-signatories.component')
          .then(m => m.AuthorizedSignatoriesComponent),
        canActivate: [canActivateRouteGuard],
      },
    ],
  },
  {
    path: 'update-user-profile',
    loadComponent: () => import('./pages/update-user-profile/update-user-profile.component')
      .then(m => m.UpdateProfileComponent),
    canActivate: [canActivateRouteGuard],
    data: {
      navBreadcrumbsTitle: 'Profile',
    },
  },
  {
    path: 'change-password',
    loadComponent: () => import('./pages/change-password/change-password.component')
      .then(m => m.ChangePasswordComponent),
    canActivate: [canActivateRouteGuard],
    data: {
      navBreadcrumbsTitle: 'Profile',
    },
  },
  {
    // TODO: variables from the grid view can be passed via paramsOrQueryParamsChange in addres bar
    path: 'edit-template',
    component: EditTemplateComponent,
    runGuardsAndResolvers: 'pathParamsOrQueryParamsChange',
    canActivate: [canActivateRouteGuard],
    canDeactivate: [editTemplateDeactivateGuard],
  },
  {
    path: 'logout',
    loadComponent: () => import('./pages/logout/logout.component')
      .then(m => m.LogoutComponent),
  },
  {
    path: '**',
    redirectTo: 'dashboard',
  },
  //TODO: check and enable the following routes as they are needed:
  /*
    {
     path: "market-info",
     component: MarketInfoComponent,
     canActivate: [canActivateRouteGuard],
     title: "Market Info",
     data: {
       navBreadcrumbsTitle: "Market Info",
       requiredPermission: "market-info:view"
     },
   },
   {
     path: "notifications",
     component: NotificationsComponent,
     canActivate: [canActivateRouteGuard],
     title: "Notifications",
     data: {
       navBreadcrumbsTitle: "Notifications",
       requiredPermission: "notifications:view"
     },
   },
   {
     path: "templates",
     canActivate: [canActivateRouteGuard],
     title: "Templates",
     data: {
       navBreadcrumbsTitle: "Templates",
       requiredPermission: "templates:view"
     },
     children: [
       {
         path: "",
         component: TemplatesComponent,
         canActivate: [canActivateRouteGuard],
       },
       {
         path: "new-template",
         component: NewTemplateComponent,
         canActivate: [canActivateRouteGuard],
         title: "New Template",
         data: {
           navBreadcrumbsTitle: "New Templates",
           requiredPermission: "templates:upload-templates",
         },
       },
     ],
   },*/
  /*
   {
     path: "document-fields",
     canActivate: [canActivateRouteGuard],
     title: "Document Fields",
     data: {
       navBreadcrumbsTitle: "Document Fields",
       requiredPermission: "mdt:view-document-fields",
     },
     children: [
       {
         path: "",
         component: DocumentFieldsComponent,
         canActivate: [canActivateRouteGuard],
       },
       {
         path: "bulk-upload",
         component: DocumentFieldsUploadComponent,
         canActivate: [canActivateRouteGuard],
         title: "Document Fields bulk upload",
         data: {
           navBreadcrumbsTitle: "Bulk upload",
           requiredPermission: "mdt:update-document-fields",
         },
       },
     ],
   },
   {
     path: "activity",
     component: ActivityComponent,
     canActivate: [canActivateRouteGuard],
     title: "Activity",
     data: {
       navBreadcrumbsTitle: "Activity",
     }
   },
   {
     path: "groups",
     component: GroupsComponent,
     canActivate: [canActivateRouteGuard],
     title: "Groups",
     data: {
       navBreadcrumbsTitle: "Groups",
       requiredPermission: "groups:view"
     },
   },
   {
     path: "data-scope",
     component: DataScopesComponent,
     canActivate: [canActivateRouteGuard],
     title: "DataScope",
     data: {
       navBreadcrumbsTitle: "DataScope",
       requiredPermission: "datascopes:view"
     },
   },
  */
];

routes.forEach((route) => {
  if (route.title && typeof route.title === 'string')
    route.title += ' - Euronext Securities Tax System';

  if (route.children) 
    route.children.forEach((childRoute) => {
      if (childRoute.title && typeof childRoute.title === 'string')
        childRoute.title += ' - Euronext Securities Tax System';
    });
})

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
